import { useState } from "react";
import { PageHeader } from "../../page-header";
import { usePagamentoLote } from "../../../hooks/usePagamentoLote";
import { formatValueToMonetary } from "../../../utils/formatter";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import { getPixInfo } from "../../../services/pix.service";
import { identificarTipoChavePix } from "../../../utils/identificarTipoChavePix";
import { banco_codigo as bancoCodigo } from "../../../assets/banco_codigo";
import "../PagamentoLote.css";
import "./PagamentoLoteNovo.css";

const PAYMENT_FIELDS = {
  PIX: [
    { id: "paymentDetail", label: "Chave Pix", placeholder: "CPF, CNPJ, e-mail, telefone ou chave aleatória" },
  ],
  Boleto: [
    { id: "paymentDetail", label: "Código de barras / linha digitável", placeholder: "Digite ou cole o código do boleto" },
    { id: "supplierData",  label: "Cedente", placeholder: "Nome do cedente do boleto" },
  ],
};

const EMPTY_FORM = {
  type: "PIX",
  payee: "",
  document: "",
  amount: "",
  dueDate: "",
  costCenter: "",
  paymentDetail: "",
  supplierData: "",
  invoice: "",
  note: "",
  // TED specific
  tedBank: "",
  tedAgency: "",
  tedAccount: "",
  tedDigit: "",
  tedAccountType: "0",
};

function dateBR(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr + "T00:00:00").toLocaleDateString("pt-BR");
}

function typeIcon(type) {
  const icons = { Boleto: "▥", PIX: "❖", Tributo: "▣", TED: "↔" };
  return icons[type] || "▣";
}

export default function PagamentoLoteNovo() {
  const { addPayment, createBatch, payments, batches } = usePagamentoLote();
  const [form, setForm] = useState(EMPTY_FORM);
  const [mode, setMode] = useState("novo");
  const [selectedBatchId, setSelectedBatchId] = useState("");
  // PIX key validation
  const [pixKeyStatus, setPixKeyStatus] = useState("idle"); // idle | validating | valid | invalid
  const [pixKeyReceiver, setPixKeyReceiver] = useState(null);

  const openBatches = batches.filter(
    (b) => b.status === "RASCUNHO" || b.status === "AGUARDANDO_APROVACAO"
  );

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    // reset pix validation whenever key or type changes
    if (name === "paymentDetail" || name === "type") {
      setPixKeyStatus("idle");
      setPixKeyReceiver(null);
    }
  }

  async function validatePixKey(key) {
    if (!key) return;
    setPixKeyStatus("validating");
    setPixKeyReceiver(null);
    try {
      const response = await getPixInfo(key, identificarTipoChavePix(key));
      const data = response?.data?.data;
      setPixKeyReceiver(data?.ReceiverName || "Titular encontrado");
      setPixKeyStatus("valid");
    } catch {
      setPixKeyStatus("invalid");
    }
  }

  function clearForm() {
    setForm(EMPTY_FORM);
    setPixKeyStatus("idle");
    setPixKeyReceiver(null);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const { type, payee, document, amount, dueDate, paymentDetail, supplierData,
            tedBank, tedAgency, tedAccount, tedDigit, tedAccountType } = form;

    if (!type || !payee || !document || !amount || !dueDate) {
      toast.error("Preencha todos os campos obrigatórios.");
      return;
    }
    if (type === "PIX" && !paymentDetail) {
      toast.error("Preencha todos os campos obrigatórios.");
      return;
    }
    if (type === "Boleto" && (!paymentDetail || !supplierData)) {
      toast.error("Preencha todos os campos obrigatórios.");
      return;
    }
    if (type === "TED" && (!tedBank || !tedAgency || !tedAccount || !tedDigit)) {
      toast.error("Preencha todos os campos do TED.");
      return;
    }
    if (type === "PIX" && pixKeyStatus !== "valid") {
      toast.error("Valide a chave Pix antes de prosseguir.");
      return;
    }
    if (mode === "existente" && !selectedBatchId) {
      toast.error("Selecione o lote ao qual deseja adicionar o pagamento.");
      return;
    }

    const tedAccountLabel = tedAccountType === "0" || tedAccountType === 0 ? "Conta Corrente" : "Conta Poupança";
    const tedBankLabel = bancoCodigo.find((b) => String(b.value) === String(tedBank))?.label || tedBank;

    const details =
      type === "PIX"
        ? paymentDetail
        : type === "Boleto"
        ? `${paymentDetail} | Cedente: ${supplierData}`
        : JSON.stringify({
            toBank: tedBank,
            toBankBranch: tedAgency,
            toBankAccount: tedAccount,
            toBankAccountDigit: tedDigit,
            accountType: Number(tedAccountType),
          });

    let batchId = mode === "existente" ? selectedBatchId : null;

    if (mode === "novo") {
      const batchResult = await createBatch();
      if (!batchResult?.ok) return;
      batchId = batchResult.batch.id;
    }

    const result = await addPayment({
      type,
      payee,
      document,
      amount: Number(amount),
      dueDate,
      costCenter: form.costCenter,
      details,
      invoice: form.invoice || null,
      note: form.note || null,
      batchId,
    });

    if (result?.ok) {
      toast.success(mode === "novo" ? "Lote criado com sucesso!" : "Conta adicionada ao lote com sucesso!");
      clearForm();
    }
  }

  const recentItems = payments.slice(0, 8);

  return (
    <div className="paylote-page">
      <div className="paylote-inner col-md-10 m-auto mt-4">
        <div className="mb-3">
          <PageHeader
            title="Novo Lote"
            pages={[
              { name: "Pagamento em Lote", link: "/pagamento-lote/dashboard" },
              { name: "Novo lote" },
            ]}
          />
        </div>

        {/* Form card */}
        <section className="paylote-section-card">
          <div className="paylote-section-header">
            <div>
              <h2>Cadastrar conta no lote</h2>
              <p className="paylote-subtitle">
                Escolha o tipo de pagamento para ver os campos específicos.
              </p>
            </div>
            <button className="paylote-btn-secondary" type="button" onClick={clearForm}>
              Limpar campos
            </button>
          </div>

          {/* Mode selector */}
          <div className="paylote-tabs" style={{ marginBottom: "1.25rem" }}>
            <button
              type="button"
              className={`paylote-tab${mode === "novo" ? " active" : ""}`}
              onClick={() => setMode("novo")}
            >
              + Criar novo lote
            </button>
            <button
              type="button"
              className={`paylote-tab${mode === "existente" ? " active" : ""}`}
              onClick={() => setMode("existente")}
            >
              Adicionar a lote existente
            </button>
          </div>

          {mode === "existente" && (
            <div className="paylote-form-field paylote-novo-batch-select">
              <label>Selecionar lote *</label>
              <select value={selectedBatchId} onChange={(e) => setSelectedBatchId(e.target.value)}>
                <option value="">— Selecione um lote aberto —</option>
                {openBatches.map((b) => (
                  <option key={b.id} value={b.id}>
                    #{b.id} — {b.createdByName} — {b.totalBills} contas ({b.status})
                  </option>
                ))}
              </select>
              {openBatches.length === 0 && (
                <span className="paylote-novo-field-hint">Nenhum lote aberto disponível no momento.</span>
              )}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Common fields */}
            <div className="paylote-form-grid">
              <div className="paylote-form-field">
                <label>Tipo de pagamento *</label>
                <select name="type" value={form.type} onChange={handleChange}>
                  <option value="PIX">PIX</option>
                  <option value="Boleto">Boleto</option>
                  <option value="TED">TED</option>
                </select>
              </div>
              <div className="paylote-form-field">
                <label>Favorecido / fornecedor *</label>
                <input name="payee" value={form.payee} onChange={handleChange} placeholder="Nome do fornecedor" />
              </div>
              <div className="paylote-form-field">
                <label>CPF / CNPJ *</label>
                <input name="document" value={form.document} onChange={handleChange} placeholder="00.000.000/0001-00" />
              </div>
              <div className="paylote-form-field">
                <label>Valor *</label>
                <input name="amount" type="number" min="0" step="0.01" value={form.amount} onChange={handleChange} placeholder="0,00" />
              </div>
              <div className="paylote-form-field">
                <label>Vencimento *</label>
                <input name="dueDate" type="date" value={form.dueDate} onChange={handleChange} />
              </div>
              <div className="paylote-form-field">
                <label>Centro de custo</label>
                <input name="costCenter" value={form.costCenter} onChange={handleChange} placeholder="Ex: Marketing, Operações..." />
              </div>
            </div>

            {/* Dynamic fields by type */}
            {form.type !== "TED" ? (
              <div className="paylote-form-grid two">
                {(PAYMENT_FIELDS[form.type] || PAYMENT_FIELDS.PIX).map((field) => (
                  <div className="paylote-form-field" key={field.id}>
                    <label>{field.label}{field.optional ? "" : " *"}</label>
                    <input
                      name={field.id}
                      value={form[field.id]}
                      onChange={handleChange}
                      placeholder={field.placeholder}
                      onBlur={
                        field.id === "paymentDetail" && form.type === "PIX"
                          ? () => validatePixKey(form.paymentDetail)
                          : undefined
                      }
                    />
                    {field.id === "paymentDetail" && form.type === "PIX" && (
                      <>
                        {pixKeyStatus === "validating" && (
                          <span className="paylote-pix-hint paylote-pix-validating">⏳ Validando chave Pix...</span>
                        )}
                        {pixKeyStatus === "valid" && (
                          <span className="paylote-pix-hint paylote-pix-valid">✓ {pixKeyReceiver}</span>
                        )}
                        {pixKeyStatus === "invalid" && (
                          <span className="paylote-pix-hint paylote-pix-invalid">✗ Chave Pix não encontrada</span>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              /* TED specific fields */
              <div className="paylote-form-grid">
                <div className="paylote-form-field paylote-ted-bank">
                  <label>Banco *</label>
                  <select name="tedBank" value={form.tedBank} onChange={handleChange}>
                    <option value="">— Selecione o banco —</option>
                    {bancoCodigo.map((b) => (
                      <option key={b.value} value={b.value}>{b.label}</option>
                    ))}
                  </select>
                </div>
                <div className="paylote-form-field">
                  <label>Agência *</label>
                  <input
                    name="tedAgency"
                    value={form.tedAgency}
                    onChange={handleChange}
                    placeholder="0000"
                    maxLength={6}
                  />
                </div>
                <div className="paylote-form-field">
                  <label>Conta *</label>
                  <input
                    name="tedAccount"
                    value={form.tedAccount}
                    onChange={handleChange}
                    placeholder="00000000"
                    maxLength={20}
                  />
                </div>
                <div className="paylote-form-field paylote-ted-digit">
                  <label>Dígito *</label>
                  <input
                    name="tedDigit"
                    value={form.tedDigit}
                    onChange={handleChange}
                    placeholder="0"
                    maxLength={1}
                  />
                </div>
                <div className="paylote-form-field">
                  <label>Tipo de conta *</label>
                  <select name="tedAccountType" value={form.tedAccountType} onChange={handleChange}>
                    <option value="0">Conta Corrente</option>
                    <option value="1">Conta Poupança</option>
                  </select>
                </div>
              </div>
            )}

            {/* Note and invoice */}
            <div className="paylote-form-grid two">
              <div className="paylote-form-field">
                <label>Nota fiscal</label>
                <input name="invoice" value={form.invoice} onChange={handleChange} placeholder="Número da NF, link do PDF ou chave de acesso (opcional)" />
              </div>
              <div className="paylote-form-field">
                <label>Descrição / Observação</label>
                <input name="note" value={form.note} onChange={handleChange} placeholder="Ex: pagamento referente à campanha de maio" />
              </div>
            </div>

            <div className="paylote-novo-form-actions">
              <button className="paylote-btn-success" type="submit">
                {mode === "novo" ? "+ Criar lote com esta conta" : "+ Adicionar ao lote selecionado"}
              </button>
              <Link to="/pagamento-lote/aprovacao" className="paylote-btn-secondary paylote-novo-link">
                Ver aprovação
              </Link>
            </div>
          </form>
        </section>

        {/* Preview table */}
        <section className="paylote-table-card">
          <div className="paylote-selection-bar">
            <strong className="paylote-novo-section-title">
              Contas cadastradas neste lote ({payments.length})
            </strong>
          </div>
          <table>
            <thead>
              <tr>
                <th>Favorecido</th>
                <th>Tipo</th>
                <th>Vencimento</th>
                <th>Valor</th>
                <th>Status</th>
                <th>Nota fiscal</th>
              </tr>
            </thead>
            <tbody>
              {recentItems.length === 0 ? (
                <tr><td colSpan={6}>
                  <div className="paylote-empty-state">
                    <p>Nenhuma conta cadastrada ainda.</p>
                  </div>
                </td></tr>
              ) : recentItems.map((item) => (
                <tr key={item.id}>
                  <td>
                    <div className="paylote-payee-name">{item.payee}</div>
                    <div className="paylote-payee-doc">{item.document}</div>
                  </td>
                  <td>
                    <span className="paylote-type-badge">
                      {typeIcon(item.type)} {item.type}
                    </span>
                  </td>
                  <td className={item.situation === "Vencido" ? "paylote-novo-due-vencido" : "paylote-novo-due-normal"}>
                    {dateBR(item.dueDate)}
                  </td>
                  <td><strong>{formatValueToMonetary(item.amount)}</strong></td>
                  <td>
                    <span className={`paylote-badge ${(item.status || "pendente").toLowerCase()}`}>
                      {item.status || "Pendente"}
                    </span>
                  </td>
                  <td className="paylote-novo-invoice-muted">{item.invoice || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </div>
    </div>
  );
}
